// Copyright 2018 Sorama B.V.
import "bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import "reflect-metadata";
import "tippy.js/dist/tippy.css";

/************
 * Aurelia *
 ************/

import { Aurelia, FrameworkConfiguration } from "aurelia-framework";
import { PLATFORM } from "aurelia-pal";

/************
 * Overrides and Polyfills *
 ************/
import { FullscreenApiPolyfill } from "overrides/polyfills/FullscreenApiPolyfill";
import { ParseNumberPolyfill } from "overrides/polyfills/ParseNumberPolyfill";

/************
 * Extensions *
 * **********/
import "extensions/CanvasExtensions";

/************
 * Libraries *
 ************/
// Authentication
import { OpenIdConnectConfiguration } from "aurelia-open-id-connect";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { OpenIDAuthentication } from "libraries/Authentication/OpenIDAuthentication";
import { oidConfiguration } from "libraries/Authentication/OpenIDConfiguration";

// WebRequestHandler
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { WebRequestHandler } from "libraries/WebRequestHandler/WebRequestHandler";

// WebAPI
import { ILocalApi } from "libraries/WebApi/ILocalApi";

/**********
 * Typings *
 **********/
import { CaptureDevice, FrontEnd } from "datatypes/SoramaLocalApi";

/***********
 * Services *
 ***********/
// Cache

// Hardware
import { MeasurementDeviceRepository } from "repositories/IndexedDbRepositories";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { LocalHardwareService } from "services/Hardware/LocalHardwareService";
import { CachedMeasurementDeviceManagement } from "services/Hardware/Management/CachedMeasurementDeviceManagement";
import { IMeasurementDeviceManagement } from "services/Hardware/Management/IMeasurementDeviceManagement";
import { MeasurementDeviceManagement } from "services/Hardware/Management/MeasurementDeviceManagement";
import { RemoteMeasurementDeviceManagement } from "services/Hardware/Management/RemoteMeasurementDeviceManagement";
import { MeasurementDeviceService } from "services/Hardware/MeasurementDeviceService";
import { ReservableHardwareService } from "services/Hardware/ReservableHardwareService";
import { CamIV64ApiMock } from "../test/services/Hardware/CamIV64ApiMock";
import { CamIV64ServiceMock } from "../test/services/Hardware/CamIV64ServiceMock";
import { MeasurementDeviceServiceMock } from "../test/services/Hardware/MeasurementDeviceServiceMock";
import { CamIV64AcquisitionClientApi } from "./services/Hardware/CamHTTP/CamIVAcquisitionClientApi";

// Images
import { CloudImageService } from "services/Image/CloudImageService";
import { IImageRetriever, IImageService } from "services/Image/IImageService";
import { ImageRetriever } from "./services/Image/ImageRetriever";

// License
import { ILicenseService } from "services/License/ILicenseService";
import { LicenseService } from "services/License/LicenseService";

// License Bundle
import { ILicenseBundleService } from "services/LicenseBundle/ILicenseBundleService";
import { LicenseBundleService } from "services/LicenseBundle/LicenseBundleService";

// Client
import { ClientService } from "services/Client/ClientService";
import { IClientService } from "services/Client/IClientService";

// Server
import { IServerStatusService } from "services/Server/IServerStatusService";
import { ServerStatusService } from "services/Server/ServerStatusService";

// Analysis
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { MeasurementProcessorResolver } from "services/Measurement/MeasurementProcessorResolver";

import { DatalessCloudSoundProcessingService } from "services/SoundProcessing/DatalessCloudSoundProcessingService";
import { IHolographyHologramProcessingService } from "services/SoundProcessing/IHolographyProcessingService";

import { ExportService } from "services/Export/ExportService";
import { IExportService } from "services/Export/IExportService";

// Measurement
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { IMeasurementManagementService } from "services/Measurement/IMeasurementManagementService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { IMeasurementStorageService } from "services/Measurement/IMeasurementStorageService";
import { MeasurementCollectionService } from "services/Measurement/MeasurementCollectionService";
import { MeasurementManagementService } from "services/Measurement/MeasurementManagementService";
import { MeasurementStorageService } from "services/Measurement/MeasurementStorageService";

// Project
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { ProjectManagementService } from "services/Project/ProjectManagementService";

// Channel selection
import { ChannelSelectionPresetService } from "services/ChannelSelection/ChannelSelectionPresetService";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";

// User settings
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { UserSettingsService } from "services/UserManagement/UserSettingsService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { UnitSystem } from "units/UnitSystem";

// Notifications
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { NotificationService } from "services/Notification/NotificationService";

// Storage
import { PsychoAcousticsCacheService } from "services/Cache/PsychoAcousticsCacheService";
import { IMeasurementVideoFileService } from "services/Storage/IMeasurementVideoFileService";
import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";
import { IStorageService } from "services/Storage/IStorageService";
import { LocalVideoFileService } from "services/Storage/LocalVideoFileService";
import { RecordingGroupContainerStorageService } from "services/Storage/RecordingGroupContainerStorageService";
import { StorageService } from "services/Storage/StorageService";

// Colors
import { CategoricalColorPalettes } from "services/Color/CategoricalColorPalettes";
import { MeasurementColorService } from "services/Color/MeasurementColorService";

// Colormaps
import { ColorMapService } from "services/ColorMap/ColorMapService";
import { IColorMapService } from "services/ColorMap/IColorMapService";

// Credits
import { CreditsService } from "services/Credits/CreditsService";
import { ICreditsService } from "services/Credits/ICreditsService";

// WebRequests
import { HttpClient as FetchHttpClient } from "aurelia-fetch-client";
import { MediaQueries } from "libraries/MediaQueries/MediaQueries";

// Icons
import { IconsInstaller } from "resources/IconsInstaller";

// Playable Properties
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { WorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog";
import { IUrlStateService } from "libraries/WorkflowFramework/Interfaces/IUrlStateService";
import { IWorkflowRouter } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { UrlStateService } from "libraries/WorkflowFramework/UrlStateService";
import { WorkflowRouter } from "libraries/WorkflowFramework/WorkflowRouter/WorkflowRouter";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { PlayablePropertiesService } from "services/PlayableProperties/PlayablePropertiesService";

// Canvas
import {
    ComposableCanvasController,
    IComposableCanvasController,
} from "controls/composable-canvas/ComposableCanvasController";
import { Chameleon } from "libraries/Chameleon/Chameleon";
import {
    CanvasMediaObjectLinkRepository,
    ICanvasMediaObjectLinkRepository,
} from "repositories/CanvasMediaObjectLinkRepository";
import { CanvasRepository, ICanvasRepository } from "repositories/CanvasRepository";
import { ILinkedMediaObjectRepository, LinkedMediaObjectRepository } from "repositories/LinkedMediaObjectRepository";
import {
    IProjectCanvasCollectionRepository,
    ProjectCanvasCollectionRepository,
} from "repositories/ProjectCanvasCollectionRepository";

// Reporting
import { SorXLDComputationService } from "modules/workflows/Measurement/SpecialMeasurements/Templates/SorXReports/SorXLDOverviewTemplate/SorXLDComputationService";
import { ReportExService } from "services/ReportEx/ReportExService";
import { PrpdDataService } from "./services/ReportEx/PrpdDataService";

// Translations
import { TCustomAttribute } from "aurelia-i18n";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";

// Miscellaneous
import { GoogleAnalytics } from "libraries/GoogleAnalytics/GoogleAnalytics";

/************************************
 * Flags *
 ************************************/
const ENABLE_MOCK_IV64 = false;
const ENABLE_MOCK_CAM64 = false;

/************************************
 * Dependency Injector Configuration *
 ************************************/
function install(aurelia: Aurelia): void {
    // WorkflowFramework
    //aurelia.container.registerSingleton(WorkflowRouter);
    aurelia.container.registerAlias(WorkflowRouter, IWorkflowRouter); // WorkflowRouter is also resolved as ViewModel. Make sure that this returns the same instance as resolving IWorkflowRouter
    aurelia.container.registerSingleton(IWorkflowModuleCatalog, WorkflowModuleCatalog);

    aurelia.container.registerInstance(OpenIdConnectConfiguration, oidConfiguration);
    aurelia.container.registerSingleton(IAuthentication, OpenIDAuthentication);
    aurelia.container.registerAlias(IAuthentication, OpenIDAuthentication);

    aurelia.container.registerSingleton(IWebRequestHandler, WebRequestHandler);
    aurelia.container.registerSingleton(IProjectManagementService, ProjectManagementService);
    aurelia.container.registerSingleton(MeasurementCollectionService);
    aurelia.container.registerSingleton(IChannelSelectionPresetService, ChannelSelectionPresetService);
    aurelia.container.registerSingleton(ILicenseService, LicenseService);
    aurelia.container.registerSingleton(ILicenseBundleService, LicenseBundleService);
    aurelia.container.registerSingleton(IClientService, ClientService);
    aurelia.container.registerAlias(IClientService, ILocalApi);
    aurelia.container.registerSingleton(IServerStatusService, ServerStatusService);
    aurelia.container.registerSingleton(IMeasurementDeviceManagement, MeasurementDeviceManagement);
    aurelia.container.registerSingleton("CachedFrontEndManagement", CachedMeasurementDeviceManagement);
    aurelia.container.registerSingleton("RemoteFrontEndManagement", RemoteMeasurementDeviceManagement);
    aurelia.container.registerSingleton(IMeasurementProcessorResolver, MeasurementProcessorResolver);
    aurelia.container.registerSingleton(IUserSettingsService, UserSettingsService);
    aurelia.container.registerSingleton(UnitSystem, UnitSystem);
    aurelia.container.registerSingleton(WorkflowModeService);
    aurelia.container.registerInstance(
        MeasurementColorService,
        new MeasurementColorService(CategoricalColorPalettes.TableauExtendedPortal())
    );
    aurelia.container.registerSingleton(IColorMapService, ColorMapService);
    aurelia.container.registerSingleton(ICreditsService, CreditsService);
    aurelia.container.registerSingleton(IStorageService, StorageService);
    aurelia.container.registerSingleton(IMeasurementVideoFileService, LocalVideoFileService);
    aurelia.container.registerSingleton(IRecordingGroupContainerStorageService, RecordingGroupContainerStorageService);
    aurelia.container.registerSingleton(IMeasurementStorageService, MeasurementStorageService);
    aurelia.container.registerSingleton(INotificationService, NotificationService);
    aurelia.container.registerSingleton(IExportService, ExportService);
    aurelia.container.registerSingleton(IPlayablePropertiesService, PlayablePropertiesService);
    aurelia.container.registerSingleton(IHolographyHologramProcessingService, DatalessCloudSoundProcessingService);
    aurelia.container.registerSingleton(IImageService, CloudImageService);
    aurelia.container.registerSingleton(IUrlStateService, UrlStateService);
    aurelia.container.registerSingleton(ICanvasRepository, CanvasRepository);
    aurelia.container.registerSingleton(IProjectCanvasCollectionRepository, ProjectCanvasCollectionRepository);
    aurelia.container.registerSingleton(ILinkedMediaObjectRepository, LinkedMediaObjectRepository);
    aurelia.container.registerSingleton(ICanvasMediaObjectLinkRepository, CanvasMediaObjectLinkRepository);
    aurelia.container.registerSingleton(IComposableCanvasController, ComposableCanvasController);
    aurelia.container.registerTransient(SorXLDComputationService);
    aurelia.container.registerTransient(PrpdDataService);
    aurelia.container.registerSingleton(ReportExService);
    aurelia.container.registerSingleton(FrequencySelectionService);
    aurelia.container.registerSingleton(PsychoAcousticsCacheService);

    aurelia.container.registerSingleton(GoogleAnalytics);

    // Check if fetch is supported.
    if (!aurelia.container.get(MediaQueries).isFetchCompatible) {
        aurelia.container.registerInstance(FetchHttpClient, null);
    }

    // Resolve the local api so it can start finding the acquisition service
    const localApi = aurelia.container.get(ILocalApi);

    aurelia.container.registerSingleton(MeasurementDeviceRepository);
    aurelia.container.registerAlias(
        MeasurementDeviceRepository,
        "IGenericRepository<MeasurementDevice,MeasurementDevice>"
    );

    // Dirty hack to be able to register the hardware service with two different interfaces:
    // Even more dirtier hack to register generic implementations

    const frontEndService = new ReservableHardwareService<FrontEnd>(
        aurelia.container.get(IWebRequestHandler),
        () => localApi.frontEnds
    );
    aurelia.container.registerInstance("IReservableHardwareService<FrontEnd,string>", frontEndService);
    aurelia.container.registerInstance("IHardwareService<FrontEnd>", frontEndService);

    const captureDeviceService = new ReservableHardwareService<CaptureDevice>(
        aurelia.container.get(IWebRequestHandler),
        () => localApi.captureDevices
    );
    aurelia.container.registerInstance("IReservableHardwareService<CaptureDevice,string>", captureDeviceService);
    aurelia.container.registerInstance("IHardwareService<CaptureDevice>", captureDeviceService);

    if (ENABLE_MOCK_CAM64) {
        aurelia.container.registerInstance("IHardwareService<MeasurementDevice>", new MeasurementDeviceServiceMock());
    } else {
        aurelia.container.registerSingleton("IHardwareService<MeasurementDevice>", MeasurementDeviceService);
    }
    aurelia.container.registerSingleton(IMeasurementManagementService, MeasurementManagementService);

    const managementService = aurelia.container.get(IMeasurementManagementService);
    aurelia.container.registerInstance(IMeasurementPreviewService, managementService);
    aurelia.container.registerInstance(IMeasurementAcquisitionService, managementService);

    const localHardwareService = new LocalHardwareService<FrontEnd>(
        aurelia.container.get(IWebRequestHandler),
        () => localApi.frontEnds
    );
    aurelia.container.registerInstance("LocalHardwareService<FrontEnd>", localHardwareService);

    let camIV64Api: ICamApi;
    let camIV64Service: CamIV64Service;

    if (ENABLE_MOCK_IV64) {
        camIV64Service = new CamIV64ServiceMock(
            camIV64Api,
            aurelia.container.get(IWebRequestHandler),
            () => localApi.frontEnds
        );
        camIV64Api = new CamIV64ApiMock(camIV64Service);
    } else {
        camIV64Service = new CamIV64Service(
            camIV64Api,
            aurelia.container.get(IWebRequestHandler),
            () => localApi.frontEnds
        );
        camIV64Api = new CamIV64AcquisitionClientApi(
            aurelia.container.get(PrpdDataService),
            aurelia.container.get(IWebRequestHandler),
            () => localApi.camIV64
        );
    }
    aurelia.container.registerInstance(CamIV64Service, camIV64Service);
    aurelia.container.registerInstance(ICamApi, camIV64Api);

    aurelia.container.registerSingleton(ImageRetriever, ImageRetriever);
    aurelia.container.registerAlias(ImageRetriever, IImageRetriever);

    IconsInstaller.installIcons();
}

// Function to configure Aurelia
export function configure(aurelia: Aurelia): FrameworkConfiguration {
    const urlParams = new URLSearchParams(window.location.search);

    const configuration = aurelia.use
        .standardConfiguration()
        .globalResources(PLATFORM.moduleName("controls/color-picker/MeasurementColorPicker")) // Required for using this control in Slickgrid
        .plugin("aurelia-event-aggregator")
        .plugin("aurelia-pal-browser")
        .plugin(PLATFORM.moduleName("aurelia-open-id-connect"), () => oidConfiguration)
        .plugin(PLATFORM.moduleName("aurelia-slickgrid"))
        .plugin(PLATFORM.moduleName("aurelia-dialog"))
        .plugin(PLATFORM.moduleName("aurelia-validation"))
        .plugin(PLATFORM.moduleName("aurelia-i18n"), (instance) => configureI18n(instance, urlParams));

    if (SORAMA_DEBUG || urlParams.has("debug")) {
        console.log("Running in Debug");
        configuration.developmentLogging("debug");
    }

    initializeApplication(aurelia);

    return configuration;
}

function configureI18n(instance, urlParams): void {
    const aliases = ["t", "i18n"];
    TCustomAttribute.configureAliases(aliases);

    // register i18next backend plugin
    instance.i18next.use(Backend);

    // register auto detect language
    instance.i18next.use(LanguageDetector);

    // adapt options to your needs (see https://www.i18next.com/overview/configuration-options)
    // make sure to return the promise of the setup method, in order to guarantee proper loading
    return instance.setup({
        backend: {
            loadPath: "./locales/{{lng}}/{{ns}}.json",
            addPath: "./translations/add/{{lng}}/{{ns}}",
        },
        attributes: aliases,
        fallbackLng: "en",
        debug: SORAMA_DEBUG || urlParams.has("debug"),
        keySeparator: false,
        saveMissing: false,
        saveMissingTo: "fallback",
    });
}

import { UnitConverters } from "units/UnitConverters";

export function initializeApplication(aurelia: Aurelia): void {
    // Initialize unit converters before starting the application
    UnitConverters.registerConverters();

    aurelia
        .start()
        .then(() => {
            install(aurelia);
            return aurelia.setRoot(PLATFORM.moduleName("modules/shell/App/App"));
        })
        // Use polyfills after the document has properly loaded
        .then(() => {
            initializePolyfills();
            checkSupportedBrowser(aurelia);
            triggerUploads(aurelia);
            initializeChameleon(aurelia);
            initializeGoogleAnalytics(aurelia);
        })
        .catch((e) => {
            console.log(`Unable to load Aurelia: ${e}`);
        });
}

function initializePolyfills(): void {
    new FullscreenApiPolyfill().use();
    new ParseNumberPolyfill().use();
}

// Check if this is a supported browser
function checkSupportedBrowser(aurelia: Aurelia): void {
    const mediaQueries = aurelia.container.get(MediaQueries);
    if (!mediaQueries.isSupportedBrowser) {
        handleUnsupportedBrowser(aurelia);
    }
}

function handleUnsupportedBrowser(aurelia: Aurelia): void {
    const notificationService = aurelia.container.get(INotificationService);
    const notification = notificationService.registerNotification(
        "Browser not supported",
        "The current browser is not supported by the Sorama Portal, some features might not work as expected",
        NotificationUrgency.Warning
    );
    notification.setOnClickListener(() => {
        const manager = aurelia.container.get(ShellViewManager);
        manager.openRightSideBar("supported_browsers").catch((e) => {
            console.log(`Unable to open sidebar: ${e}`);
        });
    });
}

// Get the measurement storage service to trigger uploads
function triggerUploads(aurelia: Aurelia): void {
    aurelia.container.get(IMeasurementStorageService);
}

function initializeChameleon(aurelia: Aurelia): void {
    Chameleon.initialize();
    Chameleon.setAuthentication(aurelia);
}

function initializeGoogleAnalytics(aurelia: Aurelia): void {
    aurelia.container.get(GoogleAnalytics).initialize();
}
